import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';

// @material-ui/core components

import {
  getClients,
  isClientActive,
} from 'services/client';

import { can } from 'services/permission';

// core components
import GridItem from 'components/Grid/GridItem';
import GridContainer from 'components/Grid/GridContainer';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import Button from 'components/CustomButtons/Button';
import { ConsoleContext } from 'services/context';
import ClientFilters from './components/ClientFilters';

export default function ClientList() {
  const { showError, showLoader } = useContext(ConsoleContext);

  const [canEdit, setCanEdit] = useState(false);
  const [clients, setClients] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ status: 'active' });

  const columns = [
    { id: 'companyName', label: 'Company Name' },
    { id: 'name', label: 'Client Name' },
    { id: 'manager', label: 'Account Manager' },
  ];

  useEffect(() => {
    async function fetchData() {
      showLoader(true);

      setCanEdit(await can('general:clients:edit'));

      try {
        const clients = await getClients();
        setClients(clients);
      } catch (error) {
        showError(error);
      }

      showLoader(false);
    }

    fetchData();
  }, []);

  useEffect(() => {
    if (filters.status && clients.length) {
      const clientsFiltered = clients.filter(
        (client) => ((filters.status === 'active' && isClientActive(client))
        ) || (client.status === 'archived' && filters.status === 'archived'),
      );

      const rows = clientsFiltered.map((item) => ({
        id: item._id,
        companyName: item.companyName,
        name: `${item.firstName || '-'} ${item.lastName || '-'}`,
        manager: item.managerId ? (`${item.managerId.firstName} ${item.managerId.lastName}`) : '-',
      }));

      setRows(rows);
    }
  }, [clients, filters]);

  // Memoize handlers to avoid creating new functions on each render
  const handleChangePage = React.useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = React.useCallback((event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  }, []);

  const handleFiltersChange = React.useCallback((filters) => {
    setFilters(filters);
    setPage(0);
  }, []);

  return (
    <div>
      <GridContainer>
        <GridItem xs={12} sm={12} md={12}>
          <Card>
            <CardBody>
              <GridContainer>
                <GridItem xs={12} sm={12} md={8}>
                  <ClientFilters
                    onChange={handleFiltersChange}
                  />
                </GridItem>
                { canEdit ? (
                  <GridItem xs={12} sm={12} md={4}>
                    <Link to="/general/clients/create">
                      <Button color="primary" style={{ float: 'right' }}>+ Create Client</Button>
                    </Link>
                  </GridItem>
                ) : null }
              </GridContainer>
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>
      <GridContainer>
        <GridItem xs={12} sm={12} md={12}>
          <Card>
            <CardBody>
              <GridContainer>
                <GridItem xs={12} sm={12} md={12}>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          {columns.map((column) => (
                            <TableCell
                              key={column.id}
                              style={column.width ? { width: column.width } : {}}
                            >
                              {column.label}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                          <TableRow key={row.id}>
                            {columns.map((column) => {
                              const value = row[column.id];

                              switch (column.id) {
                                case 'companyName':
                                  return (
                                    <TableCell key={column.id}>
                                      { canEdit ? (
                                        <Link to={`/general/clients/${row.id}`}>{value}</Link>
                                      ) : value }
                                    </TableCell>
                                  );

                                default:
                                  return (
                                    <TableCell key={column.id}>
                                      { value }
                                    </TableCell>
                                  );
                              }
                            })}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    rowsPerPageOptions={[20, 50, 100]}
                    component="div"
                    count={rows.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onChangePage={handleChangePage}
                    onChangeRowsPerPage={handleChangeRowsPerPage}
                  />
                </GridItem>
              </GridContainer>
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>
    </div>
  );
}
